/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-undef */
import * as React from "react";
import { BaseButton, Button } from "@fluentui/react";
import { GroupedList, IGroup, IGroupHeaderProps } from "@fluentui/react/lib/GroupedList";
import { ITheme, mergeStyleSets, getTheme, getFocusStyle } from "@fluentui/react/lib/Styling";
import { inject, observer } from "mobx-react";
import { Conversation, views } from "./types";
import Header from "./Header";
import { modelNames } from "./types";
import {
  Button as Buttons,
  Dialog,
  DialogTrigger,
  DialogTitle,
  DialogSurface,
  DialogBody,
  DialogActions,
  Link,
  Toaster,
  useToastController,
  useId,
  Spinner,
  Toast,
  ToastTitle,
  ToastTrigger,
  ToastBody,
} from "@fluentui/react-components";
import { ensureError, reportError } from "./utilities";
import { DocumentIcon, GPTIcon, DeleteIcon } from "..";
import Store from "./store";

const theme: ITheme = getTheme();
const { palette, semanticColors, fonts } = theme;

type DeleteCornfirmState = {
  show: boolean;
  id: string | null;
};

const classNames = mergeStyleSets({
  itemCell: [
    getFocusStyle(theme, { inset: -1 }),
    {
      minHeight: 54,
      maxHeight: 154,
      padding: "5px 5px 5px 1.2em",
      boxSizing: "border-box",
      display: "flex",
      selectors: {
        "&:hover": { background: palette.neutralLight },
      },
    },
  ],
  itemContent: {
    marginLeft: 8,
    overflow: "hidden",
    flexGrow: 1,
    alignSelf: "center",
    textOverflow: "ellipsis",
  },
  itemName: [
    fonts.medium,
    {
      whiteSpace: "nowrap",
      overflow: "hidden",
      cursor: "pointer",
      display: "inline",
      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
    },
  ],
  itemSelector: {
    display: "flex",
    width: "85%",
    minWidth: 0,
    alignItems: "center",
  },
  delete: {
    alignSelf: "center",
    marginLeft: 10,
    color: palette.red,
    fontSize: fonts.mediumPlus.fontSize,
    flexShrink: 0,
    selectors: {
      "&:hover": { background: palette.neutralLight, color: palette.red },
    },
  },
  deleteDialog: {
    display: "flex",
    justifyContent: "space-evenly",
    gap: "1em",
  },
  message: {
    alignSelf: "center",
    marginLeft: 10,
    color: palette.neutralPrimary,
    fontSize: fonts.mediumPlus.fontSize,
    flexShrink: 0,
    selectors: {
      "&:hover": { background: palette.neutralLight, color: palette.black },
      "&.standard": { color: "#D04A02" },
      "&.document": { color: "#295477" },
    },
  },
  header: {
    marginLeft: 10,
    marginTop: 5,
    marginBottom: 5,
    color: palette.neutralPrimary,
    fontSize: fonts.mediumPlus.fontSize,
  },
  confirmationArea: {
    display: "flex",
    minWidth: 0,
    marginRight: "1em",
  },
  historyContainer: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  listing: {
    overflow: "auto",
  },
  modelIcon: { margin: "0.8em" },
  loading: { width: "100%", position: "absolute", top: "50%", transform: "translate(0, -50%)", zIndex: 1 },
});

const oneDay = 1000 * 60 * 60 * 24;

function formGroups(data: Conversation[], token: string): IGroup[] {
  // Groupings for history view chat listing
  const groupLevels: IGroup[] = [
    { key: "today", name: "Today", startIndex: null, count: 0 },
    { key: "last7days", name: "Last 7 Days", startIndex: null, count: 0 },
    { key: "last30days", name: "Last 30 Days", startIndex: null, count: 0 },
    { key: "over30days", name: "Over 30 days", startIndex: null, count: 0 },
  ];

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    data.forEach((chat, index) => {
      const chatDate = new Date(chat.TimeStamp); // UTC
      const offset = chatDate.getTimezoneOffset();
      const diff = today.getTime() - (chatDate.getTime() - offset * 60 * 1000); // Timestamps are UTC -> Converting to users own locale
      let group: IGroup;
      switch (true) {
        case diff < 0: {
          group = groupLevels[0];
          break;
        }
        case diff <= oneDay * 7: {
          group = groupLevels[1];
          break;
        }
        case diff < oneDay * 30: {
          group = groupLevels[2];
          break;
        }
        case diff >= oneDay * 30: {
          group = groupLevels[3];
          break;
        }
      }

      group.count = group.count + 1;
      if (group.startIndex === null) {
        group.startIndex = index;
      }
    });
    return groupLevels.filter((level) => level.startIndex !== null);
  } catch (error: unknown) {
    const causeError = ensureError(error);
    const reportingError = new Error(
      `ConversationGrouping.Cause${causeError.message
        .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
        .replace(/\s/g, "")}`
    );
    reportError(reportingError, token);
    return groupLevels;
  }
}

const HistoryView = inject("store")(
  observer(({ store }: { store: Store }) => {
    const username = store.getEmail();
    const token = store.getToken();
    const [showConfirmation, setShowConfirmation] = React.useState<DeleteCornfirmState>({ show: false, id: null });
    const [loading, setLoading] = React.useState<boolean>(true);
    const historyUrl = "https://kafka-legal-analyst-be-stg.pwc.fi/get_messages/" + username;
    const deleteUrl = "https://kafka-legal-analyst-be-stg.pwc.fi/delete_conversation";
    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);

    const conversationGroups = formGroups(store.conversations, store.bearerToken);

    const alertUser = (errorMsg: string) => {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle
            action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>
            }
          >
            Oh no!
          </ToastTitle>
          <ToastBody>{errorMsg}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    };

    React.useEffect(() => {
      setLoading(true);
      Promise.all([
        fetch(historyUrl, {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(historyUrl + "_docs", {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET",
            Authorization: `Bearer ${token}`,
          },
        }),
      ])
        .then(([standard, document]) => Promise.all([standard.json(), document.json()]))
        .then(([standard, document]) => {
          let combined: Conversation[] = standard.concat(document);
          combined = combined
            .sort(
              (a: Conversation, b: Conversation) => new Date(a.TimeStamp).getTime() - new Date(b.TimeStamp).getTime()
            )
            .reverse();

          store.setConversations(combined);
          setLoading(false);
        })
        .catch((error: unknown) => {
          const causeError = ensureError(error);
          alertUser("Failed to fetch your history");
          const reportingError = new Error(
            `FetchingProcessingConversationsInHistory.Cause${causeError.message
              .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
              .replace(/\s/g, "")}`
          );
          reportError(reportingError, token);
        });
    }, [store.view]);

    const convertModelType = (modelType: string): modelNames => {
      if (modelType === "With docs (beta)") {
        return "With docs" as modelNames;
      }
      return modelType as modelNames;
    };

    const openItem = (
      event: React.MouseEvent<
        HTMLDivElement | HTMLAnchorElement | HTMLButtonElement | BaseButton | Button | HTMLSpanElement
      >,
      item: Conversation
    ) => {
      item.ModelType = convertModelType(item.ModelType);

      store.setSelectedConversation(
        store.conversations.find((conversation: Conversation) => item.ConversationID === conversation.ConversationID, {
          ClientID: store.getClientID(),
          ConversationID: null,
          ModelType: item.ModelType,
          Content: [],
          Summary: "",
          Filters: {},
          TimeStamp: "",
        })
      );
      store.setModel(item.ModelType);
      store.setView(views.chat);

      event.stopPropagation();
    };

    const removeItem = async (
      event: React.MouseEvent<
        HTMLDivElement | HTMLAnchorElement | HTMLButtonElement | BaseButton | Button | HTMLSpanElement
      >,
      item: Conversation
    ) => {
      await fetch(deleteUrl, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "POST",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversationID: parseInt(item.ConversationID),
        }),
      }).catch((error: unknown) => {
        const causeError = ensureError(error);
        alertUser("Error in deleting a conversation.");
        const reportingError = new Error(
          `DeleteConversation${item.ConversationID}.Cause${causeError.message
            .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
            .replace(/\s/g, "")}`
        );
        reportError(reportingError, token);
      });
      store.setConversations(
        store.conversations.filter((conversation: Conversation) => conversation.ConversationID !== item.ConversationID)
      );
      event.stopPropagation();
    };

    const showClick = (
      event: React.MouseEvent<
        HTMLDivElement | HTMLAnchorElement | HTMLButtonElement | BaseButton | Button | HTMLSpanElement
      >,
      item: Conversation
    ) => {
      setShowConfirmation({ show: true, id: item.ConversationID });
      event.stopPropagation();
    };

    const cancelClick = (
      event: React.MouseEvent<
        HTMLDivElement | HTMLAnchorElement | HTMLButtonElement | BaseButton | Button | HTMLSpanElement
      >
    ) => {
      setShowConfirmation({ show: false, id: null });
      event.stopPropagation();
    };

    const onRenderCell = (_nestingDepth?: number, item?: Conversation, _itemIndex?: number): JSX.Element | null => {
      return item ? (
        <div className={classNames.itemCell} data-is-focusable={true}>
          <div className={classNames.itemSelector} onClick={(e) => openItem(e, item)}>
            {item.ModelType == modelNames.baseModelName ? (
              <GPTIcon className={classNames.modelIcon} />
            ) : (
              <DocumentIcon className={classNames.modelIcon} />
            )}
            <div className={classNames.itemContent}>
              <div className={classNames.itemName}>{item.Summary}</div>
            </div>
          </div>
          <Dialog>
            <DialogTrigger>
              <Buttons
                className={classNames.delete}
                icon={<DeleteIcon />}
                onClick={(e) => showClick(e, item)}
                appearance="transparent"
              />
            </DialogTrigger>
            <DialogSurface style={{ width: "80%" }}>
              <DialogBody>
                <DialogTitle>Delete conversation</DialogTitle>
                <DialogActions>
                  <div className={classNames.deleteDialog}>
                    <DialogTrigger>
                      <Buttons appearance="primary" onClick={(e) => removeItem(e, item)} style={{ width: "100%" }}>
                        Confirm
                      </Buttons>
                    </DialogTrigger>
                    <DialogTrigger disableButtonEnhancement>
                      <Buttons appearance="secondary" onClick={cancelClick} style={{ width: "100%" }}>
                        Cancel
                      </Buttons>
                    </DialogTrigger>
                  </div>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      ) : null;
    };

    const onRenderHeader = (props?: IGroupHeaderProps): JSX.Element | null => {
      if (props) {
        return <div className={classNames.header}>{props.group!.name}</div>;
      }
      return null;
    };

    const groupedListProps = {
      onRenderHeader,
    };

    return (
      <div className={classNames.historyContainer}>
        <Header store={store} />
        {loading && store.conversations.length === 0 ? (
          <Spinner className={classNames.loading} size="huge" label="Loading" labelPosition="before" />
        ) : (
          <GroupedList
            items={store.conversations}
            onRenderCell={onRenderCell}
            groupProps={groupedListProps}
            groups={conversationGroups}
            className={classNames.listing}
          />
        )}
        <Toaster toasterId={toasterId} limit={1} />
      </div>
    );
  })
);

export default HistoryView;
